import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import "moment/locale/ar";
import { useNavigate } from "react-router-dom";
import { EndPoints } from "../../../Constants/endpoints";
import i18n from "../../../i18n";
import { useChangeAppointmentStatusMutation } from "../../../api/Appointments/changeAppointmentStatus";
import { useState } from "react";
import { useToast } from "../../../Services/ToastContext"; 
import { Modal, GeneralImage, GeneralButton } from "../../../components";
import trash from "../../../assets/images/icons/delete-icon.svg";
import { usePostData } from "../../../hooks";
import { INotification } from "../../../interface/GeneralResponse";
import { useTranslation } from "react-i18next";
import { refetchAllAppointmentsApis } from "../../../api/Appointments/refetchAllAppointmentsApis";
import { AppointmentCancelConfirm } from "../../Appointments/component";

const NotificationItem: React.FC<{
  item: INotification;
  onClose?: () => void;
  noTrash?: boolean;
}> = ({ item, onClose, noTrash }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleShareClick = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const unSeenClass = item?.Seen ? "bg-primary-bg" : "";
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { mutate: mutateMarkAsSeen } = usePostData(
    `${EndPoints.MarkAsSeen}/${item?.Id}`,
  );

  const [isCancelConfirmOpen, setIsCancelConfirmOpen] =
    useState<boolean>(false);
  const [confirmCancelStatus, setConfirmCancelStatus] = useState<
    "Reject" | "Cancel"
  >();
  const { mutate: mutateDelete } = usePostData(
    `${EndPoints.DeleteNotifications}/${item?.Id}`,
  );

  const handleDeleteNotification = () => {
    try {
      mutateDelete(
        {},
        {
          onSuccess: () => {
            showToast(t("deleted_successfully"), "success");
            queryClient.refetchQueries({
              queryKey: [EndPoints.Notifications],
              exact: false,
            });
          },
          onError: (error) => {
            showToast(error.message, "error");
          },
        },
      );
    } catch (error) {
      showToast(t("failed_to_delete"), "error");
    }
  };

  const { mutate, isPending } = useChangeAppointmentStatusMutation({
    onSuccess: () => {
      setIsCancelConfirmOpen(false);
      if (
        confirmCancelStatus === "Reject" ||
        confirmCancelStatus === "Cancel"
      ) {
        showToast(
          t(
            confirmCancelStatus === "Reject"
              ? "appointment_rejected_successfully"
              : "appointment_canceled_successfully",
          ),
          "success",
        );
        setConfirmCancelStatus(undefined);
      } else showToast(t("appointment_approved_successfully"), "success");
    },
    onError: (error) => {
      showToast(error.message, "error");
    },
  });

  const onSubmit = (values: { Note: string }) => {
    mutate(
      {
        AppointmentId: item?.ItemId,
        action: confirmCancelStatus,
      } as any,
      {
        onSuccess: () => {
          setIsCancelConfirmOpen(false);
          refetchAllAppointmentsApis(queryClient);
        },
        onError: (error) => {
          showToast(error.message, "error");
        },
      },
    );
  };

  return (
    <>
      <div
        className={`flex cursor-pointer flex-col items-start justify-start gap-2 rounded-lg border-b border-[#f3f7ff] px-2 pb-3 pt-2 ${item.Seen ? "bg-white" : "bg-primary-bg"} ${unSeenClass}`}
      >
        <div className="flex w-full justify-between">
          <div
            className={`inline-flex gap-4`}
            onClick={() => {
              mutateMarkAsSeen(
                {},
                {
                  onSuccess: () => {
                    let route =
                      item.Type === "APPOINTMENT"
                        ? `/appointments/${item?.ItemId}`
                        : item.Type === "DOCUMENT"
                          ? "/profile/doctor_specialty"
                          : undefined;

                    if (!!route) {
                      navigate(route, { replace: true });
                      onClose && onClose();
                    }
                  },
                  onError: (error) => {
                    showToast(error.message, "error");
                  },
                },
              );
            }}
          >
            <GeneralImage
              alt="patient"
              width="2.5rem"
              height="2.5rem"
              src={item?.Image}
              className="h-10 w-10 rounded-[37.33px]"
            />
            <div className="inline-flex shrink grow basis-0 flex-col items-start gap-1 overflow-hidden">
              <div className="flex flex-col items-start justify-center gap-1">
                <div className="flex gap-1">
                  <span className="line-clamp-2 text-sm font-normal text-primary-text">
                    <span className="text-sm font-bold text-primary-text">
                      {item?.Title}
                    </span>

                    {` ${item?.Description}`}
                  </span>
                </div>
                <div className="text-xs font-normal text-body-text">
                  {moment(item?.CreatedAt, "DD/MM/YYYY HH:mm:ss A", "en")
                    .locale(i18n.language?.slice(0, 2))
                    .fromNow()}
                </div>
              </div>
            </div>
          </div>

          {!noTrash && (
            <img
              src={trash}
              alt="delete"
              className="h-5 w-5 cursor-pointer"
              onClick={handleShareClick}
            />
          )}
        </div>
        <div className={`ms-auto flex min-w-[10rem] justify-end gap-2`}>
          {item?.Type === "APPOINTMENT" && item?.HasAction && (
            <div className={`ms-auto flex min-w-[10rem] justify-end gap-2`}>
              <>
                <GeneralButton
                  title={t("reject")}
                  color="lite"
                  className="h-fit !rounded-[0.25rem] border border-[#ff2c2c2f] !bg-error-00 !px-[0.95rem] !py-[0.2rem] !text-sm !text-error"
                  onClick={(event) => {
                    onClose && onClose();
                    navigate(`/appointments/${item?.ItemId}`, {
                      state: "reject",
                    });
                    event.preventDefault();
                    event.stopPropagation();
                    setConfirmCancelStatus("Reject");
                  }}
                  isLoading={isPending && confirmCancelStatus === "Reject"}
                  disabled={isPending && confirmCancelStatus !== "Reject"}
                />
                <GeneralButton
                  className="h-fit !rounded-[0.25rem] border border-primary !px-12 !py-[0.2rem] !text-sm"
                  title={t("confirm")}
                  color="blue"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    mutate({
                      AppointmentId: item?.ItemId,
                      action: "Approve",
                    } as any);
                  }}
                  isLoading={isPending && confirmCancelStatus !== "Reject"}
                  disabled={isPending && confirmCancelStatus === "Reject"}
                />
              </>
            </div>
          )}
        </div>
        {isCancelConfirmOpen && (
          <AppointmentCancelConfirm
            status="Reject"
            isOpen={isCancelConfirmOpen}
            onClose={() => {
              setIsCancelConfirmOpen(false);
            }}
            onSubmit={onSubmit}
            isLoading={isPending}
          />
        )}
      </div>
      {isModalOpen && (
        <Modal
          header={t("delete")}
          isOpen={isModalOpen}
          onClose={closeModal}
          customStyle="sm:w-[450px]"
        >
          <div className="test-wrap text-lg text-body-text">
            <span className="mx-2">
              {t("are_you_sure_that_you_want_to_delete")}
            </span>
            <span className="mx-2 font-bold italic text-black" dir="rtl">
              {item?.Description}
            </span>
            <span className="mx-2">{t("from_your_list")}</span>
          </div>
          <div className="mt-5 flex gap-2">
            <GeneralButton
              title={t("cancel")}
              fontColor="body-text"
              color="lite"
              onClick={closeModal}
              className="!w-[180px]"
            />
            <GeneralButton
              title={t("delete")}
              color="liteRed"
              onClick={() => {
                handleDeleteNotification();
                closeModal();
              }}
              className="w-full"
            />
          </div>
        </Modal>
      )}
    </>
  );
};
export default NotificationItem;
